import React, { useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Upload, Loader2, Trash2, ImageIcon, FileText } from 'lucide-react';

// One editable image slot. Uploads via the Core.UploadFile integration, then
// upserts the matching PageMedia record (create or update by page+slot) and
// asks the parent to refetch.
export default function PageMediaUploader({ page, slot, record, defaultUrl, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const currentUrl = record?.image_url || defaultUrl;
  const isOverride = !!record;
  const isPdf = slot.kind === 'pdf';
  const acceptAttr = isPdf ? 'application/pdf' : 'image/*';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isPdf) {
      if (file.type !== 'application/pdf') {
        setError('Please choose a PDF file.');
        return;
      }
    } else if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const up = await base44.integrations.Core.UploadFile({ file });
      const url = up.file_url;
      if (record) {
        await base44.entities.PageMedia.update(record.id, { image_url: url });
      } else {
        await base44.entities.PageMedia.create({
          page_key: page,
          slot_key: slot.key,
          image_url: url,
          alt_text: slot.label,
        });
      }
      onSaved?.();
    } catch (err) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!record) return;
    if (!confirm('Remove this override and revert to the default image?')) return;
    setBusy(true);
    try {
      await base44.entities.PageMedia.delete(record.id);
      onSaved?.();
    } catch (err) {
      setError(err?.message || 'Failed to remove.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#0d0d0f] border border-white/5 p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-white text-sm font-bold">{slot.label}</p>
          <p className="text-white/30 text-[11px] font-mono mt-0.5">
            {slot.key} · {slot.aspect} · {slot.recommended}
          </p>
        </div>
        {isOverride && (
          <span className="text-[10px] font-bold tracking-widest text-primary border border-primary/30 bg-primary/10 px-2 py-0.5">
            OVERRIDE
          </span>
        )}
      </div>

      <div className={`relative ${isPdf ? 'aspect-[3/2]' : 'aspect-video'} bg-[#111115] border border-white/5 overflow-hidden mb-3`}>
        {currentUrl ? (
          isPdf ? (
            <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-2 text-primary hover:bg-primary/5 transition-colors">
              <FileText className="w-10 h-10" />
              <span className="text-xs font-bold tracking-wide">VIEW CURRENT PDF</span>
            </a>
          ) : (
            <img src={currentUrl} alt={slot.label} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            {isPdf ? <FileText className="w-8 h-8" /> : <ImageIcon className="w-8 h-8" />}
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
      </div>

      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept={acceptAttr} onChange={handleFile} className="hidden" />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white text-xs font-bold tracking-wide px-4 py-2.5 transition-colors disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" /> {isOverride ? 'REPLACE' : 'UPLOAD'}
        </button>
        {isOverride && (
          <button
            type="button"
            disabled={busy}
            onClick={handleRemove}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wide px-4 py-2.5 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> REVERT
          </button>
        )}
      </div>
    </div>
  );
}